<template>
    <div class="pt-6">
        <!-- Payer Details Section -->
        <div v-if="attendee" class="payer-details mb-6">
            <h4 class="text-xl font-semibold mb-4">{{ $translate('Payer Details') }}</h4>
            <div class="p-4 bg-gray-100 rounded-md">
                <p class="payer-detail"><strong>{{ $translate('Name') }}:</strong> {{ attendee?.user?.name ?? '' }}</p>
                <p class="payer-detail"><strong>{{ $translate('E-mail') }}:</strong> {{ attendee?.user?.email ?? '' }}
                </p>
            </div>
            <div class="invoice-note">
                <p class="p-2">{{ $translate('We will send you the invoice to the email address %s.',
                    [attendee?.user?.email ?? '']) }}<br>
                    {{ $translate('After receiving payment, we will automatically send' +
                        ' confirmation of participation and tickets to all participants.') }}</p>
            </div>
        </div>

        <!-- Address Sections -->
        <div class="flex flex-wrap gap-4">
            <!-- Billing Address -->
            <div class="address-section bg-white p-6 rounded-lg shadow-lg w-full md:w-1/2 mx-auto">
                <div class="flex items-center justify-between mb-4">
                    <h4 class="text-lg font-semibold">{{ $translate('Billing Address') }}</h4>
                    <button type="button" @click="editBillingAddress" class="edit-btn">{{ $translate('Edit') }}</button>
                </div>
                <div v-if="billingAddress && billingAddress.name">
                    <p>{{ billingAddress.name }}</p>
                    <p>{{ billingAddress.address }}</p>
                    <p>{{ billingAddress.city }}, {{ billingAddress.zip }}</p>
                    <p>{{ billingAddress.country }}</p>
                    <p><strong>{{ $translate('Company Number') }}:</strong> {{ billingAddress.ico }}</p>
                    <p><strong>{{ $translate('Company VAT Number') }}:</strong> {{ billingAddress.dic }}</p>
                </div>
                <div v-else class="text-red-500">
                    <p>{{ $translate('Billing address is not available.') }}</p>
                </div>
            </div>

            <!-- Shipping Address -->
            <div v-if="cartStore.state.shippingRequired"
                class="address-section bg-white p-6 rounded-lg shadow-lg w-full md:w-1/2">
                <div class="flex items-center justify-between mb-4">
                    <h4 class="text-lg font-semibold">{{ $translate('Shipping Address') }}</h4>
                    <button type="button" @click="editShippingAddress" class="edit-btn">{{ $translate('Edit')
                        }}</button>
                </div>
                <div v-if="shippingAddress && shippingAddress.name">
                    <p>{{ shippingAddress.name }}</p>
                    <p>{{ shippingAddress.address }}</p>
                    <p>{{ shippingAddress.city }}, {{ shippingAddress.zip_code }}</p>
                    <p>{{ shippingAddress.country }}</p>
                </div>
                <div v-else class="text-red-500">
                    <p>{{ $translate('Shipping address is not available.') }}</p>
                </div>
            </div>
        </div>

        <!-- Voucher Section -->
        <div class="mt-6 voucher-section">
            <h4 class="text-xl font-semibold mb-4">{{ $translate('Apply Voucher, Gift Card, or Coupon') }}</h4>
            <div class="flex items-center">
                <div class="relative flex-1">
                    <input v-model="voucherCode" type="text" :placeholder="$translate('Enter Code')"
                        class="input-field" />
                </div>
                <button type="button" @click="applyVoucher" class="apply-btn ml-3">
                    {{ $translate('Apply') }}
                </button>
            </div>
        </div>
    </div>
    <BillingAddressModal v-if="showBillingModal" :address="billingAddress" @close="closeModal"
        @save="saveBillingAddress" />
    <ShippingAddressModal v-if="showShippingModal" :address="shippingAddress" @close="closeModal"
        @save="saveShippingAddress" />

</template>

<script>
import { inject, ref, computed } from 'vue';
import { cookie } from '@/utils/cookie';
import { notify } from 'notiwind';
import ShippingAddressModal from '@/components/modals/ShippingAddressModal.vue';
import BillingAddressModal from '@/components/modals/BillingAddressModal.vue';
import { translate } from '@/utils/translate';

export default {
    name: 'RecapitulationPage',
    components: { BillingAddressModal, ShippingAddressModal },
    props: {
        attendee: {
            type: Object,
            required: false,
        },
    },
    setup(props, { emit }) {

        const cartStore = inject('cartStore');
        if (!cartStore) {
            throw new Error('cartStore is not provided');
        }

        // Use computed properties for reactive updates

        const billingAddress = computed(() => props.attendee?.billing_address || {});
        const shippingAddress = computed(() => props.attendee?.shipping_address || {});
        const voucherCode = ref('');
        const showBillingModal = ref(false);
        const showShippingModal = ref(false);
        const sessionId = cookie(); // Replace with actual session ID

        const confirm = () => {
            emit('confirm');
        };

        const removeItem = (item) => {
            cartStore.removeItem(item.id, item.type);
            emit('remove-item', item);
        };

        const editBillingAddress = () => {
            showBillingModal.value = true;
        };

        const editShippingAddress = () => {
            showShippingModal.value = true;
        };

        const closeModal = () => {
            showBillingModal.value = false;
            showShippingModal.value = false;
        };

        const saveBillingAddress = () => {
            closeModal();
            emit('refresh-attendee');
        };
        const saveShippingAddress = () => {
            closeModal();
            emit('refresh-attendee');
        };

        const applyVoucher = async () => {
            try {
                const response = await fetch(`${window.vueAppConfig.endpoint}/api/v2/events/cart/voucher/apply`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        brand_id: window.vueAppConfig.info.id,
                        session_id: sessionId,
                        voucher_code: voucherCode.value
                    }),
                });
                const data = await response.json();
                if (data.status) {
                    notify({
                        title: translate("Success"),
                        text: data.message,
                        type: "info",
                        group: "info"
                    }, 15000);
                    // Clear the voucher input field
                    voucherCode.value = '';
                    // Reload cart content instead of directly updating the total
                    await cartStore.fetchCartContent();
                } else {
                    notify({
                        title: translate("Error"),
                        text: data.message,
                        type: "error",
                        group: "error"
                    }, 15000);
                }
            } catch (error) {
                console.error('Error applying voucher:', error);
            }
        };

        return {
            cartStore,
            billingAddress,
            shippingAddress,
            voucherCode,
            showBillingModal,
            showShippingModal,
            confirm,
            removeItem,
            editBillingAddress,
            editShippingAddress,
            closeModal,
            saveBillingAddress,
            saveShippingAddress,
            applyVoucher,
        };
    },
};
</script>

<style scoped>
/* Professional styles for the sections */
.payer-details {
    margin-bottom: 2rem;
}

.address-section {
    background-color: #fff;
    padding: 1.5rem;
    border-radius: 8px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.3s ease;
}

.address-section:hover {
    box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.15);
}

.edit-btn {
    background-color: #3b82f6;
    color: #fff;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.edit-btn:hover {
    background-color: #2563eb;
}

.voucher-section {
    background-color: #f0fff4;
    /* Light green background */
    padding: 1.5rem;
    border-radius: 8px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.05);
    /* Subtle shadow */
}

/* Input Field */
.input-field {
    width: 100%;
    padding: 0.75rem 1rem;
    /* Adjust padding for consistency with button */
    border: 1px solid #d1d5db;
    border-radius: 6px;
    transition: border-color 0.3s ease;
    font-size: 1rem;
}

.input-field:focus {
    outline: none;
    border-color: #22c55e;
    /* Match button color on focus */
}

/* Apply Button */
.apply-btn {
    background-color: #22c55e;
    /* Green button */
    color: white;
    padding: 0.75rem 1.5rem;
    /* Ensure the same height as the input */
    border-radius: 6px;
    font-size: 1rem;
    transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.apply-btn:hover {
    background-color: #16a34a;
    /* Darker green on hover */
    box-shadow: 0 4px 12px rgba(22, 163, 74, 0.2);
}

.invoice-note {
    padding: 1rem;
    background-color: #fef3c7;
    border-radius: 8px;
    margin-top: 1.5rem;
    font-size: 0.875rem;
}

@media (max-width: 768px) {
    .address-section {
        width: 100%;
    }
}

@media (max-width: 400px) {
    .payer-detail {
        font-size: 14px;
    }
}
</style>
